import { useEffect, useState, type ReactNode, type SyntheticEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ExternalLink, Link2, Lock, MonitorPlay, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Hint } from "@/components/ui/tooltip";
import { api, ApiError } from "@/lib/api";
import { useCan } from "@/lib/auth";
import { extendedTtl, openPreviewsOf, timeLeftLabel } from "@/lib/previews";
import { qk } from "@/lib/query/keys";
import { useLiveSync } from "@/lib/query/live-sync";
import type { Preview } from "@/lib/types";
import { cn } from "@/lib/utils";

const selectPreviews = (data: { previews: Preview[] }) => data.previews;
const NONE: Preview[] = [];

/** Every open preview (admins only; a viewer is never sent the list). Hits are not on the live feed, so it re-reads each minute. */
export function usePreviews(): Preview[] {
  const admin = useCan("admin");
  const query = useQuery({ queryKey: qk.previews(), queryFn: api.listPreviews, select: selectPreviews, enabled: admin, refetchInterval: 60_000 });
  return query.data ?? NONE;
}

/** The clock time left is drawn against, ticking while something shows it. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

/**
 * An agent's open dev-server previews: a pill when it has any, opening the list —
 * title and link, port, time left, page loads and who may open it — with Extend
 * (+1h / +24h, within the 24 h cap) and Close. Nothing at all when it has none.
 * `compact` is the Agents list row's form: an icon and a count.
 */
export function PreviewsButton({ agentId, compact = false, className }: { agentId: string; compact?: boolean; className?: string }) {
  const all = usePreviews();
  const [open, setOpen] = useState(false);
  const now = useNow(all.length > 0);
  const previews = openPreviewsOf(all, agentId, now);
  if (previews.length === 0) return null;
  const label = `${previews.length} open preview${previews.length === 1 ? "" : "s"}`;
  // In a list row the pill sits inside the row's button: keep its presses to itself.
  const stop = (event: SyntheticEvent) => event.stopPropagation();
  const trigger: ReactNode = compact ? (
    <span
      role="button"
      tabIndex={0}
      aria-label={label}
      onClick={stop}
      onPointerDown={stop}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") stop(event);
      }}
      className={cn(
        "inline-flex shrink-0 cursor-pointer items-center gap-0.5 rounded-full px-1.5 py-px text-[10.5px] font-medium tabular-nums",
        "bg-[color-mix(in_oklab,var(--kind-active)_16%,transparent)] text-[var(--kind-active)] hover:bg-[color-mix(in_oklab,var(--kind-active)_26%,transparent)]",
        className,
      )}
    >
      <MonitorPlay className="size-3" aria-hidden />
      {previews.length > 1 ? previews.length : "preview"}
    </span>
  ) : (
    <Button
      variant="ghost"
      size="sm"
      aria-label={label}
      className={cn("h-7 gap-1 rounded-full px-2 text-[12px] text-[var(--kind-active)] hover:text-[var(--kind-active)]", className)}
    >
      <MonitorPlay className="size-3.5" />
      <span className="hidden win-sm:inline">Preview{previews.length > 1 ? `s · ${previews.length}` : ""}</span>
      {previews.length > 1 ? <span className="tabular-nums win-sm:hidden">{previews.length}</span> : null}
    </Button>
  );
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <Hint label={label}>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      </Hint>
      <DropdownMenuContent align="end" className="w-80 max-w-[calc(100vw-1rem)]" onClick={stop} onPointerDown={stop}>
        <DropdownMenuLabel>Previews · {previews.length}</DropdownMenuLabel>
        <div className="max-h-[60dvh] space-y-1 overflow-y-auto scrollbar-slim">
          {previews.map((preview) => (
            <PreviewRow key={preview.id} preview={preview} now={now} />
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PreviewRow({ preview, now }: { preview: Preview; now: number }) {
  const { localChange } = useLiveSync();
  const done = () => localChange(["previews"]);
  const failed = (what: string) => (error: unknown) => toast.error(`Could not ${what}: ${error instanceof ApiError ? error.message : "the board did not answer"}`);
  const extend = useMutation({
    mutationFn: (minutes: number) => api.extendPreview(preview.id, extendedTtl(preview.expiresAt, minutes, Date.now())),
    onSuccess: (next) => toast.success(`${next.title ?? "Preview"} stays up ${timeLeftLabel(next.expiresAt, Date.now())}`),
    onError: failed("extend the preview"),
    onSettled: done,
  });
  const close = useMutation({
    mutationFn: () => api.closePreview(preview.id),
    onSuccess: () => toast.success(`Closed ${preview.title ?? preview.host}`),
    onError: failed("close the preview"),
    onSettled: done,
  });
  const busy = extend.isPending || close.isPending;
  const left = Date.parse(preview.expiresAt) - now;
  return (
    <div className="rounded-md px-2 py-1.5 hover:bg-muted/50">
      <a href={preview.url} target="_blank" rel="noopener noreferrer" className="group/link flex items-center gap-1.5 text-[13px] font-medium">
        <span className="truncate group-hover/link:underline">{preview.title ?? `Port ${preview.port}`}</span>
        <ExternalLink className="size-3 shrink-0 text-muted-foreground" aria-hidden />
      </a>
      <p className="truncate font-mono text-[11px] text-muted-foreground" title={preview.url}>
        {preview.host}
      </p>
      <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground tabular-nums">
        <span>:{preview.port}</span>
        <span className={cn(left < 10 * 60_000 && "font-medium text-[var(--prio-urgent)]")}>{timeLeftLabel(preview.expiresAt, now)} left</span>
        <span>
          {preview.hits} load{preview.hits === 1 ? "" : "s"}
        </span>
        <span className="inline-flex items-center gap-0.5" title={preview.access === "link" ? "Anyone with the link can open it" : "Only people signed in to the board"}>
          {preview.access === "link" ? <Link2 className="size-3" aria-hidden /> : <Lock className="size-3" aria-hidden />}
          {preview.access === "link" ? "anyone with link" : "signed in"}
        </span>
      </p>
      <div className="mt-1.5 flex items-center gap-1">
        <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" disabled={busy} onClick={() => extend.mutate(60)}>
          +1h
        </Button>
        <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" disabled={busy} onClick={() => extend.mutate(1440)}>
          +24h
        </Button>
        <Button size="sm" variant="ghost" className="ml-auto h-6 gap-1 px-2 text-[11px] text-[var(--prio-urgent)]" disabled={busy} onClick={() => close.mutate()}>
          <X className="size-3" />
          Close
        </Button>
      </div>
    </div>
  );
}
